import type { PropertyFilterProps } from '@risk-smart/themed-cloudscape-components';
import type { FormFieldConfig } from '@risksmart-app/shared/forms/types';

export type ConditionOperators = NonNullable<
  PropertyFilterProps['filteringProperties'][number]['operators']
>;

/**
 * Cloudscape's `filterByToken` returns false for any operator a property does
 * not declare, so a free-text source declaring none silently evaluates every
 * rule except `=` to false (CUS-373).
 */
export const FREE_TEXT_CONDITION_OPERATORS: ConditionOperators = [
  '=',
  '!=',
  ':',
  '!:',
];

// `link` is excluded: its standard displayType declares only `=`, and widening
// it would also change the reporting and table property filters.
const FREE_TEXT_CONDITION_FIELD_TYPES: readonly string[] = ['text', 'textarea'];

export const isFreeTextConditionType = (type: string | undefined): boolean =>
  FREE_TEXT_CONDITION_FIELD_TYPES.includes(type ?? '');

// No `displayType` at all means free-text capture (e.g. Risk `Title`).
export const standardFieldSupportsContains = (
  field: Pick<FormFieldConfig, 'displayType'> | undefined
): boolean =>
  !!field &&
  (!field.displayType ||
    isFreeTextConditionType(field.displayType.displayType));

// An explicitly empty `operators` means "supports none" — never widen it.
export const freeTextOperatorDefaults = (
  type: string | undefined,
  declared: { operators?: ConditionOperators } | undefined
): { operators?: ConditionOperators } =>
  declared?.operators !== undefined || !isFreeTextConditionType(type)
    ? {}
    : { operators: [...FREE_TEXT_CONDITION_OPERATORS] };
